import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Menu from "./components/menu";
import React, { lazy, Suspense } from "react";
import { Button } from "@heroui/button";
import { MoonStar, Sun } from "lucide-react";
import { Toaster } from "react-hot-toast";
import DefaultLayout from "./layouts/default";
import CookieConsent from "@/components/cookie-consent";
import { isMobile, onMenuNavigate } from "@/stores/MenuStore.ts";
import GameList from "./components/GamesList";
import { useMenuStore } from "@/stores/MenuStore";
import { refreshUserStore, setPostHogUser, useUserStore } from "./components/login/loginStore";
import { usePostHog } from "posthog-js/react";
import { addPageView } from "@/utils/addPageView";
import { HelmetProvider } from 'react-helmet-async';
import SEO from "./components/SEO";
import LoadingPage from "./pages/loadingPage";
import Cookies from "universal-cookie";
import useRouterErrorHandling from "./utils/reloadOnFailedFetch";
import FinderPlayer from "./pages/finder";
import { refreshBypassTeams } from "./stores/bypassTeamsStore";
import FreeTeams from "./components/freeTeams";
import Referee from "./pages/referee";
import PremiumGuard from "./components/premium-guard";
import RobotipPromo from "@/components/RobotipPromo";
import TelegramBot from "./pages/telegram-bot";

const Plans = lazy(() => import('./pages/plans'));
const League = lazy(() => import('./pages/league'));
const RegisterPage = lazy(() => import('./pages/register'));
const Team = lazy(() => import('./pages/team'));
const Game = lazy(() => import('./pages/game'));
const Player = lazy(() => import('./pages/player'));
const SubscriptionInfo = lazy(() => import('./pages/my-account'));
const RedirectNotLogged = lazy(() => import('./pages/redirectNotLogged'));
const AffiliateRegistry = lazy(() => import('./pages/affiliate-registry'));
const AffiliateDashboard = lazy(() => import('./pages/affiliate-dashboard'));
const SuccessPage = lazy(() => import('./components/checkout/SuccessPage'));
const TutorialVideo = lazy(() => import('./pages/tutorial-video'));
const LeaguesSearch = lazy(() => import('./components/leagues/LeaguesSearch'));
const TeamsSearch = lazy(() => import('./components/TeamsSearch'));
const PlayersSearch = lazy(() => import('./components/PlayerSearch'));
const LoginPage = lazy(() => import('./pages/login'));
const RefereeSearch = lazy(() => import('./components/RefereeSearch'));
const AdminPage = lazy(() => import('./pages/adminPage'));

interface ToggleDarkModeElProps {
    element: HTMLElement | null;
    darkmode: boolean;
}

function toggleDarkModeEl({ element, darkmode }: ToggleDarkModeElProps) {
    if (!element) return;
    element.classList.remove("light", "dark")
    if (darkmode) {
        element.classList.add("dark");
    } else {
        element.classList.add("light");
    }
}

export function isDarkModeEnabled() {
    return localStorage.getItem("darkMode") === "true";
}

const MAIN_SITE_HOSTNAMES = ['craquestats.com.br', 'www.craquestats.com.br', 'localhost', 'matheus-test-dev.robotip.com.br'];

export function actAsSeparateWebsite() {
    return !MAIN_SITE_HOSTNAMES.includes(window.location.hostname);
}

function getSystemIsDarkMode() {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function getThemeFromGETParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const theme = urlParams.get('theme');
    if (theme === 'dark') {
        return true;
    } else if (theme === 'light') {
        return false;
    }
    return null;
}

function getDefaultDarkMode() {
    const themeFromGET = getThemeFromGETParams();
    if (themeFromGET !== null) {
        return themeFromGET;
    }
    return getSystemIsDarkMode();
}

function App() {
    useRouterErrorHandling();
    const rootRouteComponent = <GameList />;
    const isDefaultSite = !actAsSeparateWebsite();

    const location = useLocation();
    const navigate = useNavigate();
    const hideMenu = (location.pathname == "/login" || location.pathname == "/register") || !isDefaultSite;
    const [darkMode, setDarkMode] = React.useState(getDefaultDarkMode());
    const [loadedDarkMode, setLoadedDarkMode] = React.useState(false);
    const menuStore = useMenuStore((state) => state);
    const loginStore = useUserStore((state) => state);
    const posthog = usePostHog();

    React.useEffect(() => {
        addPageView();
    }, [location]);

    React.useEffect(() => {
        setPostHogUser(posthog);
        if (loginStore.logged) {
            const cookies = new Cookies();
            let redirectPath = cookies.get('redirectAfterLogin');
            if (redirectPath && typeof redirectPath === 'string') {
                redirectPath = decodeURI(redirectPath);
                if (redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
                    cookies.remove('redirectAfterLogin', { path: '/' });
                    navigate(redirectPath);
                }
                else {
                    console.warn('Invalid redirect path:', redirectPath);
                }
            }
        }
    }, [loginStore.logged]);

    React.useEffect(() => {
        const savedDarkMode = localStorage.getItem("darkMode");
        if (savedDarkMode) {
            setDarkMode(JSON.parse(savedDarkMode));
        }
        setLoadedDarkMode(true);
    }, []);

    React.useEffect(() => {
        if (loadedDarkMode)
            localStorage.setItem("darkMode", JSON.stringify(darkMode));
    }, [darkMode]);

    React.useEffect(() => {
        toggleDarkModeEl({
            element: document.getElementsByTagName("html")[0],
            darkmode: darkMode,
        });
    }, [darkMode]);

    React.useEffect(onMenuNavigate, [location]);

    React.useEffect(() => {
        refreshUserStore();
        refreshBypassTeams();
    }, []);


    return (
        <HelmetProvider>
            {isDefaultSite && <SEO />}
            <Toaster />
            {isDefaultSite &&
                !loginStore.premium && !hideMenu &&
                <FreeTeams />
            }
            <div
                className="cursor-pointer"
                style={{
                    position: "fixed",
                    bottom: "10px",
                    right: `${isMobile() ? '10px' : '30px'}`,
                    zIndex: "100",
                }}
                onClick={() => setDarkMode(!darkMode)}
            >
                <Button
                    className="cursor-pointer"
                    isIconOnly
                    aria-label="Toggle Dark Mode"
                    onClick={() => setDarkMode(!darkMode)}
                >
                    {darkMode ? <Sun /> : <MoonStar />}
                </Button>
            </div>
            <div className={`${(menuStore.menuOpened && isMobile()) && 'overflow-hidden'}`}>
                {!hideMenu && <Menu />}
                <DefaultLayout hideMenu={hideMenu}>
                    <Suspense fallback={<LoadingPage />}>
                        <Routes>
                            <Route element={rootRouteComponent} path="/" />
                            <Route element={<GameList />} path="/schedule" />
                            <Route
                                element={
                                    <RedirectNotLogged>
                                        <Plans />
                                    </RedirectNotLogged>
                                }
                                path="/plans"
                            />
                            <Route path=
                                "/team/:id" element={<Team />} />
                            <Route path=
                                "/league/:id" element={<League />} />
                            <Route path=
                                "/game/:id" element={<Game />} />
                            <Route path='/player/:id' element={<Player />} />
                            <Route path='/referee/:id' element={<Referee />} />
                            <Route path='/leagues' element={<LeaguesSearch cardMode={false} />} />
                            <Route path='/teams' element={<TeamsSearch cardMode={false} />} />
                            <Route element={<LoginPage />} path="/login" />
                            <Route element={<RegisterPage darkMode={darkMode} />} path="/register" />
                            <Route path={'/my-account'} element={
                                <RedirectNotLogged>
                                    <SubscriptionInfo />
                                </RedirectNotLogged>
                            } />
                            <Route path='/players' element={<PlayersSearch cardMode={false} />} />
                            <Route path='/referees' element={<RefereeSearch cardMode={false} />} />
                            <Route path='/affiliate-registry' element={
                                <RedirectNotLogged>
                                    <AffiliateRegistry />
                                </RedirectNotLogged>
                            } />
                            <Route path='/affiliate-dashboard' element={
                                <RedirectNotLogged>
                                    <AffiliateDashboard />
                                </RedirectNotLogged>
                            } />
                            <Route path='/checkout/success' element={<SuccessPage />} />
                            <Route element={<TutorialVideo />} path="/tutorial-video" />
                            <Route element={<PremiumGuard><FinderPlayer /></PremiumGuard>} path="/finder-player" />
                            {/* Super Odds pages are disabled; keep existing links pointed at home. */}
                            <Route path="/super-odds" element={<Navigate to="/" replace />} />
                            <Route path="/super-odds/:superOddsUniqueId" element={<Navigate to="/" replace />} />
                            <Route path="/super-odds/link/:encodedLink" element={<Navigate to="/" replace />} />
                            <Route path="/super-odd/:superOddsUniqueId" element={<Navigate to="/" replace />} />
                            <Route path="/super-odds-admin" element={<Navigate to="/" replace />} />
                            <Route path="/admin_panel" element={<AdminPage />} />
                            <Route path="/telegram-bot" element={<TelegramBot />} />
                        </Routes>
                    </Suspense>
                </DefaultLayout>
            </div>
            <div
                className="pointer-events-none fixed inset-x-0 bottom-0 px-[21px] pb-[26px] flex flex-col gap-4"
                style={{ zIndex: "200" }}
            >
                <CookieConsent />
                {isDefaultSite && <RobotipPromo hideMenu={hideMenu} />}
            </div>
        </HelmetProvider>
    );
}

export default App;
